import {
  createComponentMapperModel,
  createUDPUiTypeMapper,
  withCompInfo,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type {
  IShareButtonsStateRefs,
  ShareButtonsStyleProperties,
} from '../types';
import { shouldDisplayIcon, shouldDisplayLabel } from '../utils/display';
import { getAlignmentBasedFlexDirection } from '../../../common/viewer/mapper/cssMapperUtils';
import {
  linkCopiedTranslationKey,
  TranslationKeys,
  componentName,
  ARIA_LABEL_NAMESPACE,
  linkCopiedFailedTranslationKey,
} from '../constants';
import {
  castToInheritIfNeeded,
  withFallbackValues,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import type { LogicalIconPosition } from '../ShareButtons.props';
import { migrateIconPosition } from '../utils/iconPosition';
import { migrateAlign } from '../utils/align';

const props = withCompInfo<any, any>()(
  [
    'compProps',
    'compData',
    'compStylableClass',
    'externalBaseUrl',
    'translate',
  ],
  ({ compProps, compData, compStylableClass, externalBaseUrl, translate }) => {
    const translationKeys = [
      linkCopiedTranslationKey,
      linkCopiedFailedTranslationKey,
      TranslationKeys.component.label,
      TranslationKeys.manage.copy_link_default_text,
    ];
    const translations = translationKeys.reduce(
      (acc: { [key: string]: string | undefined }, key) => {
        acc[key] = translate(componentName, key);
        return acc;
      },
      {},
    );
    translations[TranslationKeys.ariaLabel] = translate(
      ARIA_LABEL_NAMESPACE,
      TranslationKeys.ariaLabel,
    );

    return {
      ...compProps,
      ...compData,
      stylableClassName: compStylableClass,
      externalBaseUrl,
      translations,
    };
  },
);

const iconPositionToFlexDirectionMap: Record<LogicalIconPosition, string> = {
  start: 'row',
  end: 'row-reverse',
  top: 'column',
  bottom: 'column-reverse',
};

type CompInfoOverrides = {
  styleProperties: Partial<ShareButtonsStyleProperties> &
    Pick<ShareButtonsStyleProperties, 'align' | 'logicalIconPosition'>;
};

export const css = withCompInfo<any, any, any, CompInfoOverrides>()(
  ['compData', 'styleProperties'],
  ({ compData, styleProperties }) => {
    const {
      buttonsAlignment = 'horizontal',
      rowsSpacing = 10,
      buttonsSpacing = 10,
      iconTextSpacing = 10,
      buttonContent = 'Text and Icon',
      horizontalDirection: legacyDirection = 'ltr',
      align,
      logicalIconPosition,
    } = styleProperties;

    const btnsFlexDirection =
      buttonsAlignment === 'vertical' ? 'column' : 'row';

    const isLegacyRTLDirection =
      compData.direction === undefined && legacyDirection === 'rtl';

    const btnsAlignmentDirection = getAlignmentBasedFlexDirection(
      buttonsAlignment,
      align,
    );

    const buttonsListCSSVariables = {
      '--btns-list-column-gap':
        buttonsAlignment === 'horizontal' ? buttonsSpacing : 0,
      '--btns-list-row-gap': rowsSpacing,
      '--btns-list-flex-direction': btnsFlexDirection,
      '--btns-list-justify-content': btnsAlignmentDirection['justify-content'],
      '--btns-list-align-items': btnsAlignmentDirection['align-items'],
    };

    const buttonCSSVariables = {
      '--btn-icon-position':
        iconPositionToFlexDirectionMap[logicalIconPosition],
      '--btn-icon-text-gap': iconTextSpacing,
      ...{
        '--btn-label-display': shouldDisplayLabel(buttonContent)
          ? 'inline-block'
          : 'none',
        '--btn-icon-display': shouldDisplayIcon(buttonContent)
          ? 'inline-block'
          : 'none',
        '--btn-tooltip-display': !shouldDisplayLabel(buttonContent)
          ? 'flex'
          : 'none',
        '--btn-copy-to-clipboard-display': !shouldDisplayLabel(buttonContent)
          ? 'none'
          : 'inline-block',
      },
    };

    const direction =
      compData.direction ?? castToInheritIfNeeded(legacyDirection, 'ltr');

    return {
      height: 'auto',
      '--direction': withSiteDirection(direction),
      '--btn-text-direction': isLegacyRTLDirection ? 'ltr' : 'inherit',
      ...(buttonsAlignment === 'vertical' && { width: 'auto' }),
      'min-width': 'min-content',
      ...buttonCSSVariables,
      ...buttonsListCSSVariables,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignmentDirection', target: 'align' }],
        enhancer: (
          legacyAlign,
          { styleProperties: { horizontalDirection: legacyDirection } },
        ) => {
          return migrateAlign(legacyAlign, legacyDirection);
        },
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'iconPosition', target: 'logicalIconPosition' }],
        enhancer: (
          iconPosition,
          { styleProperties: { horizontalDirection: legacyDirection } },
        ) => {
          return migrateIconPosition(iconPosition, legacyDirection);
        },
      },
    ]),
  ],
);

export const stateRefs = withStateRefsValues<keyof IShareButtonsStateRefs>([
  'currentUrlWithQueryParams',
  'experiments',
]);

export const uiType = createUDPUiTypeMapper('Default', 'ShareButtons');

export default createComponentMapperModel({
  props,
  css,
  stateRefs,
  uiType,
});
